import React, { useState, useCallback, useRef, useEffect } from 'react';
import './FileUpload.scss';
import { useFlowTs } from '../../utils/useFlowTs';
import { CdnFile } from '@services/core/cdn/file/file';
import { FlowFile } from 'flowjs';
import { createColumnHelper } from '@tanstack/react-table';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

export const useFileUpload = (
    files?: CdnFile[] | CdnFile,
    onSuccess?: (file: CdnFile[]) => void,
    onOneSuccess?: (file: CdnFile) => void,
    onOneDelete?: (file: CdnFile) => void,
    onOneEdit?: (file: CdnFile) => void,
    isMultiple?: boolean,
    position?: number,
) => {
    const [uploadedFiles, setUploadedFiles] = useState<CdnFile[]>([]);
    const [inProgressFiles, setInProgressFiles] = useState<FlowFile[]>([]);
    const [errorFiles, setErrorFiles] = useState<FlowFile[]>([]);
    const inputFlowRef = useRef<HTMLInputElement>(null);

    const MySwal = withReactContent(Swal);

    const documentColumnHelper = createColumnHelper<CdnFile>();

    useEffect(() => {
        if (!files) setUploadedFiles([]);
        if (files) setUploadedFiles(Array.isArray(files) ? files : [files]);
    }, [files]);

    useEffect(() => {
        if (onSuccess) onSuccess(uploadedFiles);
    }, [uploadedFiles]);

    const onFileSuccess = useCallback(
        (file: CdnFile, flowFile: FlowFile) => {
            if (position != null) {
                file.position = position;
            }
            setUploadedFiles((prevFiles) => [...prevFiles, file]);
            setInProgressFiles((prevFiles) => prevFiles.filter((f) => f.uniqueIdentifier !== flowFile.uniqueIdentifier));
            if (onOneSuccess) onOneSuccess(file);
        },
        [setUploadedFiles, onOneSuccess, position]
    );

    const onFileAdded = useCallback(
        (file: FlowFile) => {
            setInProgressFiles((prevFiles) => [...prevFiles, file]);
        },
        [setInProgressFiles]
    );

    const onFileError = useCallback(
        (file: FlowFile) => {
            setInProgressFiles((prevFiles) => prevFiles.filter((f) => f.uniqueIdentifier !== file.uniqueIdentifier));
            setErrorFiles((prevFiles) => [...prevFiles, file]);
        },
        [setErrorFiles, setInProgressFiles]
    );

    const { flow } = useFlowTs({ onSuccess: onFileSuccess, onAdded: onFileAdded, onError: onFileError });

    const startUpload = useCallback(
        (file: File) => {
            if (!file) return;

            if (inProgressFiles.length > 0 && !isMultiple) {
                return;
            }

            if (inputFlowRef.current) {
                inputFlowRef.current.value = '';
            }

            if (!isMultiple) {
                setUploadedFiles([]);
                setErrorFiles([]);
            }
            flow?.addFile(file);
        },
        [flow, inProgressFiles.length, isMultiple]
    );

    const removeFile = useCallback((file: CdnFile) => {
        MySwal.fire({
            title: 'Êtes-vous sûr de vouloir supprimer ce fichier ?',
            text: 'Cette action est irréversible',
            icon: 'warning',
            showCancelButton: true,
            iconColor: '#01539D',
            confirmButtonColor: '#01539D',
            confirmButtonText: 'Supprimer',
            cancelButtonText: 'Annuler',
        }).then((result) => {
            if (result.isConfirmed) {
                setUploadedFiles((prevFiles) => prevFiles.filter((f) => f.id !== file.id));
                if (onOneDelete) onOneDelete(file);
            }
        })
    }, []);

    const removeErrorFile = useCallback((file: FlowFile) => {
        setErrorFiles((prevFiles) => prevFiles.filter((f) => f.uniqueIdentifier !== file.uniqueIdentifier));
    }, []);

    const handleFileSelect = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (event.target.files) {
                Array.from(event.target.files).forEach((file) => {
                    startUpload(file);
                });
            }
        },
        [startUpload]
    );

    const handleDrop = useCallback(
        (event: React.DragEvent<HTMLDivElement>) => {
            event.preventDefault();
            if (event.dataTransfer.files) {
                Array.from(event.dataTransfer.files).forEach((file) => {
                    startUpload(file);
                });
            }
        },
        [startUpload]
    );

    const handleDragOver = useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }, []);

    const updateFile = useCallback((updatedFile: CdnFile) => {
        setUploadedFiles((prevFiles) => prevFiles.map((file) => (file.id === updatedFile.id ? updatedFile : file)));
    }, []);

    return {
        uploadedFiles,
        inProgressFiles,
        errorFiles,
        inputFlowRef,
        handleDrop,
        handleDragOver,
        handleFileSelect,
        removeFile,
        removeErrorFile,
        updateFile,
        documentColumnHelper,
        onOneEdit
    };
};
